import { useState, useEffect, useCallback } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import { persistOrganizationProfile } from '@saas-foundation/shared-frontend/utils/organizationContext';

const getApiBaseUrl = () => {
  if (typeof window !== 'undefined' && window.location) {
    const port = window.location.port;
    const hostname = window.location.hostname;

    if (hostname === 'localhost' && (port === '3000' || port === '3001')) {
      return 'http://localhost:8001/api/v1';
    }

    return '/api/v1';
  }

  return '/api/v1';
};

const getOrgIdHint = (user) =>
  user?.organization_id ||
  user?.organizationId ||
  localStorage.getItem('selectedOrganizationId') ||
  localStorage.getItem('organizationId');

export const useOrganization = () => {
  const { user, isAuthenticated } = useAuthContext();
  const [organization, setOrganization] = useState(null);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchOrganization = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const API_BASE_URL = getApiBaseUrl();
      const orgIdHint = getOrgIdHint(user);
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      };

      const requestCurrent = async (hint) => {
        const query = hint ? `?organization_id=${encodeURIComponent(String(hint))}` : '';
        return fetch(`${API_BASE_URL}/organizations/current${query}`, {
          method: 'GET',
          headers,
          credentials: 'include',
        });
      };

      const resolveFromResponse = async (response) => {
        if (!response.ok) {
          return null;
        }
        const data = await response.json();
        const orgData = data.organization || data.data?.organization || data;
        if (!orgData?.id || orgData.name === 'No Organization') {
          return null;
        }
        setOrganization(orgData);
        setCurrentUserRole(data.current_user_role || user?.role || null);
        persistOrganizationProfile(orgData);
        return orgData;
      };

      if (!token) {
        setError('Authentication required');
        setOrganization(null);
        setCurrentUserRole(null);
        return;
      }

      let response = await requestCurrent(orgIdHint);
      let orgData = await resolveFromResponse(response);

      if (!orgData && orgIdHint) {
        localStorage.removeItem('selectedOrganizationId');
        localStorage.removeItem('organizationId');
        response = await requestCurrent(null);
        orgData = await resolveFromResponse(response);
      }

      if (!orgData) {
        const listResponse = await fetch(`${API_BASE_URL}/organizations/`, {
          method: 'GET',
          headers,
          credentials: 'include',
        });
        if (listResponse.ok) {
          const organizations = await listResponse.json();
          const preferred =
            (orgIdHint &&
              organizations.find((item) => String(item.id) === String(orgIdHint))) ||
            organizations[0];
          if (preferred?.id) {
            setOrganization(preferred);
            setCurrentUserRole(user?.role || null);
            persistOrganizationProfile(preferred);
            return;
          }
        }
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.detail || errorData.message || 'Failed to fetch organization data');
        setOrganization(null);
        setCurrentUserRole(null);
      }
    } catch (err) {
      console.error('Error fetching organization:', err);
      setError(err.message || 'Failed to fetch organization data');
      setOrganization(null);
      setCurrentUserRole(null);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchOrganization();
    } else {
      setOrganization(null);
      setCurrentUserRole(null);
      setLoading(false);
    }
  }, [user, isAuthenticated, fetchOrganization]);

  const refreshOrganization = async () => {
    await fetchOrganization();
  };

  return {
    organization,
    currentUserRole,
    loading,
    error,
    refreshOrganization,
    canManageOrganization: currentUserRole === 'owner' || currentUserRole === 'admin',
    isSolo: organization?.organization_type === 'solo',
    isSmallPractice: organization?.organization_type === 'small_practice',
    isMediumPractice: organization?.organization_type === 'medium_practice',
    isEnterprise: organization?.organization_type === 'enterprise',
  };
};

export default useOrganization;
